import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { Grid } from '@material-ui/core';

import { Conditional } from '@klp/conditional';
import { useAppParams } from '@klp/core/app/useAppParams';
import { Block } from '@klp/design';
import { ErrorNotification } from '@klp/errorNotification';
import { Main } from '@klp/layout';
import { Search, filterSearchResult } from '@klp/search';

import { useCurrentUser } from '../../../auth';
import { ClientRoutes } from '../../client';
import { useResetClientMutation } from '../../client/components/Select/rtkApi';
import { appSelectors } from '../../core/app';
import { Loader } from '../../design/Loader';
import { AppRoutes } from '../../routing';
import type { Bygg } from '../classes';
import { ByggCard, ByggTable, ByggListHeader } from '../components/ByggList';
import { ByggAccess, ByggRoutes } from '../config';
import { useGetByggListQuery } from '../rtkApi';

type ByggListProps = {
    showAll?: boolean;
    selectedBygg?: string;
};

export const ByggList: React.FC<ByggListProps> = ({ showAll = false }) => {
    const history = useHistory();

    const [search, setSearch] = useState('');
    const [kundesBygg, setKundesBygg] = useState<Array<Bygg>>([]);

    // Get app params (selectedClient, isAdmin)
    const appParams = useAppParams();
    const { selectedClient } = appParams;

    // Listen to app trigger to force re-render when client state changes
    useSelector(appSelectors);

    // Get Redux state
    const { currentUser, isAdmin } = useCurrentUser();

    const [resetClient] = useResetClientMutation();

    const kundeNummer = selectedClient?.kundeNummer;

    // RTK Query
    const {
        data: byggListData,
        isLoading: byggListLoading,
        error: byggListError,
    } = useGetByggListQuery(
        { kundeNummer: !showAll ? kundeNummer : undefined },
        { skip: !showAll && !kundeNummer && !ByggAccess(currentUser) },
    );

    const byggList = useMemo(() => byggListData || [], [byggListData]);

    // Search handler
    const handleSearch = (searchValue: string) => {
        setSearch(searchValue);
    };

    const handleByggSelect = (bygg: Bygg) => {
        if (
            showAll &&
            selectedClient &&
            !kundesBygg.find((b) => b.byggId === bygg.byggId)
        ) {
            resetClient();
        }

        history.push(ByggRoutes.single(bygg.byggNummer));
    };

    const handleShowAll = () => {
        setKundesBygg(byggList);
        history.push(ByggRoutes.listAll());
    };

    // No show all handler
    const handleNoShowAll = () => {
        // Navigate to standard list view
        history.push(ByggRoutes.list());
    };

    // Listen to app trigger to force re-render when client state changes
    useSelector(appSelectors);

    // Auto-redirect for single building
    useEffect(() => {
        const hasOneBuilding = !byggListLoading && byggList.length === 1;
        if (hasOneBuilding && !showAll) {
            history.push(ByggRoutes.single(byggList[0].byggNummer));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [byggListLoading, byggList, showAll]);

    const filteredList = filterSearchResult<Bygg>(byggList, search, [
        '_byggNummer',
        '_navn',
        '_adresse',
    ]);

    const hasNoBuildings = !byggListLoading && byggList.length === 0;
    const hasBuildings = !byggListLoading && byggList.length > 1;
    const foundManyBuildings = !byggListLoading && filteredList.length > 12;

    return (
        <Main>
            <Loader isLoading={byggListLoading} data-testid="loading-spinner">
                <Conditional
                    condition={ByggAccess(currentUser) || !!selectedClient}
                    errorComponent={
                        <ErrorNotification
                            title="clientSelect.errorNoClientsSelected.title"
                            description="clientSelect.errorNoClientsSelected.description"
                            backTarget={AppRoutes.mainPage()}
                            backTitle="general.goBackToFrontpage"
                            mainTarget={ClientRoutes.clientSelect()}
                            mainTitle="clientSelect.chooseClient"
                            returnUrl={true}
                            data-testid="bygg-list-no-client-selected"
                        />
                    }
                >
                    <Conditional
                        condition={!byggListError}
                        errorComponent={
                            <ErrorNotification
                                title="bygg.errorList.title"
                                description="bygg.errorList.description"
                                backTarget={AppRoutes.mainPage()}
                                backTitle="general.goBackToFrontpage"
                                data-testid="bygg-list-error"
                            />
                        }
                    >
                        <Conditional
                            condition={!hasNoBuildings}
                            errorComponent={
                                <ErrorNotification
                                    title="bygg.errorNotFound.description"
                                    description="bygg.errorNotFound.description"
                                    backTarget={AppRoutes.mainPage()}
                                    backTitle="general.goBackToFrontpage"
                                    data-testid="bygg-list-no-buildings"
                                />
                            }
                        >
                            <Conditional condition={hasBuildings}>
                                <ByggListHeader
                                    isAdmin={isAdmin}
                                    selectedClient={selectedClient}
                                    showAll={showAll}
                                    onShowAll={handleShowAll}
                                    onNoShowAll={handleNoShowAll}
                                />

                                <Conditional
                                    condition={
                                        byggList && (!selectedClient || showAll)
                                    }
                                >
                                    <Grid container={true} spacing={2}>
                                        <Grid
                                            item={true}
                                            xs={12}
                                            sm={6}
                                            md={4}
                                            lg={3}
                                        >
                                            <Search
                                                id="byggSearch"
                                                searchHandler={handleSearch}
                                                searchOnType={true}
                                                placeholder="bygg.byggSearch"
                                                data-testid="bygg-search"
                                            />
                                        </Grid>
                                    </Grid>
                                </Conditional>

                                <Grid container={true} spacing={2}>
                                    <Conditional
                                        condition={
                                            byggList &&
                                            (!foundManyBuildings ||
                                                (!!selectedClient && !showAll))
                                        }
                                    >
                                        <Conditional
                                            condition={filteredList.length > 0}
                                            errorComponent={
                                                <Grid item={true}>
                                                    <ErrorNotification
                                                        title="bygg.errorNotFound.title"
                                                        data-testid="bygg-list-no-filtered-buildings-cards"
                                                    />
                                                </Grid>
                                            }
                                        >
                                            {filteredList.map((bygg: Bygg) => (
                                                <ByggCard
                                                    key={`${bygg.byggId}-${bygg.byggNummer}-${bygg.navn}`}
                                                    bygg={bygg}
                                                    search={search}
                                                    isAdmin={isAdmin}
                                                    onByggSelect={
                                                        handleByggSelect
                                                    }
                                                    data-testid={`bygg-card-${bygg.byggId}`}
                                                />
                                            ))}
                                        </Conditional>
                                    </Conditional>

                                    <Conditional
                                        condition={
                                            byggList &&
                                            foundManyBuildings &&
                                            (!selectedClient || showAll)
                                        }
                                    >
                                        <Conditional
                                            condition={filteredList.length > 0}
                                            errorComponent={
                                                <Grid item={true}>
                                                    <ErrorNotification
                                                        title="bygg.errorNotFound.title"
                                                        data-testid="bygg-list-no-filtered-buildings-table"
                                                    />
                                                </Grid>
                                            }
                                        >
                                            <Block data-testid="bygg-list-table">
                                                <ByggTable
                                                    byggList={filteredList}
                                                    search={search}
                                                    onByggSelect={
                                                        handleByggSelect
                                                    }
                                                />
                                            </Block>
                                        </Conditional>
                                    </Conditional>
                                </Grid>
                            </Conditional>
                        </Conditional>
                    </Conditional>
                </Conditional>
            </Loader>
        </Main>
    );
};
